/**
 * Podzavihek Pregled (docs/ARHITEKTURA.md, 15.5): po projektih predvidena
 * amortizacija, porabljeno, preostanek, poraba na mesec pri sedanjih
 * odstotkih in mesecev do porabe. Porabljeno vodi v Porabo (po mesecih do
 * knjizb), poraba na mesec v Po projektih (po sredstvih do knjizb).
 */
import type { PregledPorabeProjektov } from '@koda/shared';
import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../../lib/api';
import { mesecZaPrikaz, useBesedilaProjektov } from '../../lib/besedila-projektov';
import { datum, decimalno, znesek } from '../../lib/format';
import { OpozorilaUvoza } from '../OpozorilaUvoza';

export function PregledProjektovStranke({ podjetje }: { podjetje: string }): JSX.Element {
  const b = useBesedilaProjektov();
  const [pregled, setPregled] = useState<PregledPorabeProjektov | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  useEffect(() => {
    let aktivno = true;
    api
      .projektiPregled(podjetje)
      .then((p) => {
        if (aktivno) setPregled(p);
      })
      .catch((e: unknown) => {
        if (aktivno) setNapaka(e instanceof Error ? e.message : b.napakaBranja);
      });
    return () => {
      aktivno = false;
    };
  }, [podjetje, b.napakaBranja]);

  if (napaka !== null) {
    return (
      <p className="napaka" role="alert">
        {napaka}
      </p>
    );
  }
  if (pregled === null) return <p className="drobno">{b.berem}</p>;
  const pot = (pogled: string, projekt?: string | null): string =>
    `/podjetje/${podjetje}/projekti/${pogled}${projekt == null ? '' : `?projekt=${projekt}`}`;

  return (
    <section className="kartica">
      {pregled.mesec !== null && <p className="podnaslov">{b.pregledUvod(mesecZaPrikaz(pregled.mesec))}</p>}
      {pregled.temeljnicaOdprta && pregled.mesec !== null && <p className="opozorilo">{b.temeljnicaOdprta(mesecZaPrikaz(pregled.mesec))}</p>}
      <OpozorilaUvoza opozorila={pregled.opozorila} />
      {pregled.vrstice.length === 0 ? (
        <p className="drobno">{pregled.mesec === null ? b.niPodatkov : b.niProjektov}</p>
      ) : (
        <div className="tabela-ovoj">
          <table>
            <thead>
              <tr>
                <th>{b.projekt}</th>
                <th className="stevilka">{b.predvideno}</th>
                <th className="stevilka">{b.porabljeno}</th>
                <th className="stevilka">{b.preostanek}</th>
                <th className="stevilka">{b.naMesec}</th>
                <th className="stevilka">{b.mesecevDoPorabe}</th>
                <th>{b.zakljucek}</th>
              </tr>
            </thead>
            <tbody>
              {pregled.vrstice.map((v) => (
                <tr key={v.projekt ?? 'brez'}>
                  <td>
                    {v.naziv ?? <em>{b.brezProjekta}</em>}
                    {!v.aktiven && <span className="drobno"> ({b.zakljucen})</span>}
                  </td>
                  <td className="stevilka">{v.predvideno === null ? '—' : znesek(v.predvideno)}</td>
                  <td className="stevilka">
                    {v.porabljenoNedolocljivo ? <span className="opozorilo">—</span> : <Link to={pot('poraba')}>{znesek(v.porabljeno)}</Link>}
                    {v.zacetnaPoraba !== null && v.zacetnaPorabaDo !== null && (
                      <div className="drobno">{b.zacetnoStanje(znesek(v.zacetnaPoraba), mesecZaPrikaz(v.zacetnaPorabaDo))}</div>
                    )}
                  </td>
                  <td className={v.preostanek !== null && Number(v.preostanek) < 0 ? 'stevilka negativno' : 'stevilka'}>
                    {v.preostanek === null ? '—' : znesek(v.preostanek)}
                  </td>
                  <td className="stevilka">
                    {v.naMesecZadrzan ? (
                      <span className="opozorilo" title={b.zadrzanoNamig}>
                        {b.zadrzano}
                      </span>
                    ) : v.naMesec === null ? (
                      '—'
                    ) : v.projekt === null ? (
                      znesek(v.naMesec)
                    ) : (
                      <Link to={pot('po-projektih', v.projekt)}>{znesek(v.naMesec)}</Link>
                    )}
                  </td>
                  <td className="stevilka">{v.mesecevDoPorabe === null ? '—' : decimalno(v.mesecevDoPorabe, 1)}</td>
                  <td>{v.zakljucek === null ? '—' : datum(v.zakljucek)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>{b.skupaj}</th>
                <td />
                <td className="stevilka">
                  <strong>{pregled.skupaj.porabljeno === null ? '—' : znesek(pregled.skupaj.porabljeno)}</strong>
                </td>
                <td />
                <td className="stevilka">
                  <strong>{pregled.skupaj.naMesec === null ? '—' : znesek(pregled.skupaj.naMesec)}</strong>
                </td>
                <td />
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
